import * as types from 'constants/ActionTypes';
import {
  CLOSED_VALIDATION,
  COMMERCIAL,
  MEASUREMENT_REQUEST_TYPE_IDS,
  RESIDENTIAL,
  STRUCTURE_TYPES,
  MULTIFAMILY,
  SUCCESS, ERROR,
  MEASUREMENT_REQUEST_TYPES,
  PRODUCT_IDS,
} from 'constants/AppConstants';
import { getPreSelectedProducts } from './components/Product/ProductList.util';
import { filterMeasurementRequestTypes, getDefaultMeasurementRequestTypes } from './Order.utils';
import { PANEL_TYPES } from './Order.constants';

const initSelectedProducts = {
  selectedPrimaryProduct: null,
  selectedPrimaryDeliveryProduct: null,
  selectedWallsProduct: null,
  selectedWallsDeliveryProduct: null,
  selectedOtherProducts: {},
  selectedMeasurementRequest: MEASUREMENT_REQUEST_TYPE_IDS.PrimaryPlusDetachedGarage,
  preSelectedMeasurementRequest: MEASUREMENT_REQUEST_TYPE_IDS.PrimaryPlusDetachedGarage,
};

const initOrderPageAlertState = {
  open: false,
  severity: '',
  title: '',
  message: '',
};

const initBluePrintOrderState = {
  uploadBluePrintFileError: '',
  bluePrintIdError: false,
  uploadingBluePrintFile: false,
};

const initState = {
  duplicateReportsDialogOpen: false,
  country: 'US',
  mapToastOpen: false,
  mapToastMessage: '',
  duplicateReportsData: [],
  loading: false,
  error: null,
  propertyType: RESIDENTIAL,
  preSelectedPropertyType: RESIDENTIAL,
  isComplex: false,
  solarProductsAvailability: {},
  productAvailabilityLoading: false,
  ...initSelectedProducts,
  validationMessageType: CLOSED_VALIDATION,
  preSelectedProducts: {},
  isOrderFlowStarted: false,
  ...initBluePrintOrderState,
  orderPageAlert: initOrderPageAlertState,
  showMap: true,
  // Map provider toggle
  useEmbeddedExplorer: false,
  // Panel management state
  currentPanelType: PANEL_TYPES.PRE_PANEL,
  // Property data API state
  propertyDataRequest: {
    loading: false,
    data: null,
    error: null,
  },
  propertyDataResult: {
    loading: false,
    data: null,
    error: null,
  },
  primarySubscriptionProductsWithUsage: [],
  subscriptionPlanMode: null,
  bundleUsage: null,
  subscriptionEstimatedTotal: null,
};

function getPropertyTypeMeasurementDefaults(propertyType) {
  if (propertyType === COMMERCIAL) {
    return { selectedMeasurementRequest: MEASUREMENT_REQUEST_TYPE_IDS.CommercialComplex };
  }
  if (propertyType === MULTIFAMILY) {
    return { selectedMeasurementRequest: MEASUREMENT_REQUEST_TYPE_IDS.PrimaryStructureOnly };
  }
  return {};
}

function setPropertyType(state, action) {
  if (state.propertyType === action.propertyType) return state;
  return {
    ...state,
    propertyType: action.propertyType,
    ...initSelectedProducts,
    ...getPropertyTypeMeasurementDefaults(action.propertyType),
    ...((action.propertyType === state.preSelectedPropertyType) ? state.preSelectedProducts : {}),
  };
}

function setIsComplex(state, action) {
  if (!action.isComplex) {
    return {
      ...state,
      isComplex: action.isComplex,
    };
  }

  return {
    ...state,
    isComplex: action.isComplex,
    useEmbeddedExplorer: false,
    ...(!action.isRoofUser ? {
      ...initSelectedProducts,
      propertyType: COMMERCIAL,
      selectedMeasurementRequest: MEASUREMENT_REQUEST_TYPE_IDS.CommercialComplex,
    } : {}),
  };
}

function orderPreferencesSuccess(state, action) {
  const { orderPreferencesResponse } = action;
  const measurements = {
    structureType: orderPreferencesResponse.structureType.value,
    measurementRequestType: orderPreferencesResponse.measurementRequestType.value,
  };
  const { measurementRequestType: preSelectedMeasurementRequest, structureType } = measurements;
  const preSelectedPropertyType = STRUCTURE_TYPES[structureType];
  return {
    ...state,
    ...(preSelectedMeasurementRequest && { preSelectedMeasurementRequest }),
    ...(preSelectedPropertyType && { preSelectedPropertyType }),
    ...(state.isOrderFlowStarted ? {} : { selectedMeasurementRequest: preSelectedMeasurementRequest, propertyType: preSelectedPropertyType }),
  };
}

function updateAddressSearchText(state) {
  if (state.isOrderFlowStarted) {
    return {
      ...state,
      selectedMeasurementRequest: (state.selectedPrimaryProduct?.ProductID === PRODUCT_IDS.Roof) ? 3 : state.selectedMeasurementRequest,
    };
  }

  return {
    ...state,
    selectedMeasurementRequest: state.preSelectedMeasurementRequest,
    propertyType: state.preSelectedPropertyType,
    isOrderFlowStarted: true,
    ...state.preSelectedProducts,
  };
}

function userProductsSuccess(state, action) {
  const { userProducts } = action;
  const preSelectedProducts = getPreSelectedProducts(userProducts, state.preSelectedPropertyType);
  const shouldApplyPreSelectedProducts = state.isOrderFlowStarted && Object.keys(state.preSelectedProducts).length === 0;

  return {
    ...state,
    productAvailabilityLoading: false,
    preSelectedProducts,
    ...(shouldApplyPreSelectedProducts
      ? { ...preSelectedProducts, selectedMeasurementRequest: state.preSelectedMeasurementRequest, propertyType: state.preSelectedPropertyType }
      : {}),
  };
}

function getSelectedProductId(state, updatedState) {
  return updatedState.selectedPrimaryProduct?.ProductID
    || state.selectedPrimaryProduct?.ProductID
    || updatedState.selectedWallsProduct?.ProductID
    || state.selectedWallsProduct?.ProductID;
}

function setSelectedProduct(state, action) {
  const { updatedState } = action;
  const selectedProductId = getSelectedProductId(state, updatedState);
  const isPropertyDataSelected = updatedState.selectedPrimaryProduct?.ProductID === PRODUCT_IDS.PropertyData
    || state.selectedPrimaryProduct?.ProductID === PRODUCT_IDS.PropertyData;
  const measurementRequestOptions = MEASUREMENT_REQUEST_TYPES.filter(filterMeasurementRequestTypes(
    state.propertyType,
    selectedProductId,
    isPropertyDataSelected,
  ));
  const { id: selectedValue } = (measurementRequestOptions.find(({ id }) => id === state.selectedMeasurementRequest))
    || measurementRequestOptions[0];
  const defaultMeasurementTypes = getDefaultMeasurementRequestTypes(selectedProductId);
  const nextState = {
    ...state,
    ...updatedState,
    selectedMeasurementRequest: selectedValue,
    ...defaultMeasurementTypes,
  };

  // Apply delivery last so it is never overwritten (subscription flow: uncheck = Regular = 8)
  if (Object.hasOwn(updatedState, 'selectedPrimaryDeliveryProduct')) {
    const v = updatedState.selectedPrimaryDeliveryProduct;
    nextState.selectedPrimaryDeliveryProduct = (v !== null && v !== undefined) ? Number(v) : v;
  }
  return nextState;
}

function resetOrderPage(state) {
  return {
    ...state,
    duplicateReportsDialogOpen: false,
    country: 'US',
    mapToastOpen: false,
    mapToastMessage: '',
    duplicateReportsData: [],
    loading: false,
    error: null,
    isComplex: false,
    solarProductsAvailability: {},
    ...initSelectedProducts,
    validationMessageType: CLOSED_VALIDATION,
    propertyType: state.preSelectedPropertyType || RESIDENTIAL,
    selectedMeasurementRequest: state.preSelectedMeasurementRequest || MEASUREMENT_REQUEST_TYPE_IDS.PrimaryPlusDetachedGarage,
    preSelectedMeasurementRequest: state.preSelectedMeasurementRequest,
    ...state.preSelectedProducts,
    isOrderFlowStarted: false,
    orderPageAlert: initOrderPageAlertState,
    ...initBluePrintOrderState,
    currentPanelType: PANEL_TYPES.PRE_PANEL,
    propertyDataRequest: {
      loading: false,
      data: null,
      error: null,
    },
    propertyDataResult: {
      loading: false,
      data: null,
      error: null,
    },
    useEmbeddedExplorer: false,
    subscriptionEstimatedTotal: null,
  };
}

const actionHandlers = {
  [types.SET_USE_EMBEDDED_EXPLORER]: (state, action) => ({ ...state, useEmbeddedExplorer: action.useEmbeddedExplorer }),
  [types.SET_SHOW_MAP]: (state, action) => ({ ...state, showMap: action.showMap }),
  [types.OPEN_MAP_TOAST]: (state, action) => ({ ...state, mapToastOpen: true, mapToastMessage: action.toastAlertMessage }),
  [types.CLOSE_MAP_TOAST]: (state) => ({ ...state, mapToastOpen: false, mapToastMessage: '' }),
  [types.SET_COUNTRY]: (state, action) => ({ ...state, country: action.country }),
  [types.CLOSE_DUPLICATE_DIALOG]: (state) => ({ ...state, duplicateReportsDialogOpen: false }),
  [types.ORDER_PAGE_DUPLICATE_REPORTS_SUCCESS]: (state, action) => ({
    ...state, loading: false, duplicateReportsData: action.data, duplicateReportsDialogOpen: true,
  }),
  [types.ORDER_PAGE_DUPLICATE_REPORTS_ERROR]: (state, action) => ({
    ...state, loading: false, error: action.error, duplicateReportsDialogOpen: false,
  }),
  [types.SET_PROPERTY_TYPE]: setPropertyType,
  [types.SET_IS_COMPLEX]: setIsComplex,
  [types.ORDER_PREFERENCES_SUCCESS]: orderPreferencesSuccess,
  [types.UPDATE_ADDRESS_SEARCH_TEXT]: updateAddressSearchText,
  [types.USER_PRODUCTS_SUCCESS]: userProductsSuccess,
  [types.SET_SELECTED_PRODUCT]: setSelectedProduct,
  [types.SET_SELECTED_OTHER_PRODUCTS]: (state, action) => ({ ...state, selectedOtherProducts: action.updatedSelectedOtherProducts }),
  [types.SET_SELECTED_MEASUREMENT_REQUEST]: (state, action) => ({ ...state, selectedMeasurementRequest: action.value }),
  [types.SET_SUBSCRIPTION_ESTIMATED_TOTAL]: (state, action) => ({ ...state, subscriptionEstimatedTotal: action.value ?? null }),
  [types.SET_PRODUCT_LOADING]: (state) => ({ ...state, productAvailabilityLoading: true }),
  [types.SOLAR_PRODUCTS_AVAILABILITY_SUCCESS]: (state, action) => ({
    ...state,
    solarProductsAvailability: action.solarProductsAvailability,
    productAvailabilityLoading: false,
  }),
  [types.SOLAR_PRODUCTS_AVAILABILITY_ERROR]: (state) => ({ ...state, productAvailabilityLoading: false }),
  [types.OPEN_VALIDATION_MESSAGE_DIALOG]: (state, action) => ({ ...state, validationMessageType: action.validationMessageType }),
  [types.CLOSE_VALIDATION_MESSAGE_DIALOG]: (state) => ({ ...state, validationMessageType: CLOSED_VALIDATION }),
  [types.ORDER_RESET_ERROR]: (state) => ({ ...state, error: null }),
  [types.PLACE_ORDER_SUCCESS]: resetOrderPage,
  [types.CANCEL_ORDER_SELECTION]: resetOrderPage,
  [types.RESET_FINALIZE_ORDER_PAGE]: (state, action) => ({
    ...state,
    isEditOrder: Boolean(action.isEditOrder),
    // Drop upgrade/local totals so the next checkout cannot prefer a stale value over estimated-cost.
    subscriptionEstimatedTotal: null,
  }),
  [types.RESET_EDIT_ORDER]: (state) => ({ ...state, isEditOrder: false }),
  [types.UPGRADE_REPORT_DETAILS_SUCCESS]: (state, action) => ({
    ...state,
    propertyType: action.upgradeDetails.structureType,
    isComplex: action.upgradeDetails.upgradeFromMso,
    selectedMeasurementRequest: action.upgradeDetails.measurementRequestTypeId,
  }),
  [types.ORDER_PAGE_UPLOADING_BLUEPRINT_FILE]: (state) => ({
    ...state, uploadBluePrintFileError: '', uploadingBluePrintFile: true, orderPageAlert: initOrderPageAlertState,
  }),
  [types.ORDER_PAGE_UPLOAD_BLUEPRINT_FILE_SUCCESS]: (state) => ({
    ...state,
    uploadingBluePrintFile: false,
    isOrderFlowStarted: true,
    orderPageAlert: {
      open: true,
      severity: SUCCESS,
      message: 'File uploaded',
    },
  }),
  [types.ORDER_PAGE_UPLOAD_BLUEPRINT_FILE_ERROR]: (state, action) => ({
    ...state,
    uploadBluePrintFileError: action.error,
    uploadingBluePrintFile: false,
    orderPageAlert: {
      open: true,
      severity: ERROR,
      message: 'Upload failed',
    },
  }),
  [types.ORDER_PAGE_UPDATE_BLUEPRINT_ID]: (state, action) => ({ ...state, bluePrintIdError: !(action.bluePrintId) }),
  [types.LOADING_FINALIZE_ORDER]: (state) => ({ ...state, isOrderFlowStarted: true }),
  [types.SET_PANEL_TYPE]: (state, action) => ({ ...state, currentPanelType: action.panelType }),
  [types.PROPERTY_DATA_REQUEST_LOADING]: (state) => ({
    ...state,
    propertyDataRequest: {
      loading: true,
      data: null,
      error: null,
    },
  }),
  [types.PROPERTY_DATA_REQUEST_SUCCESS]: (state, action) => ({
    ...state,
    propertyDataRequest: {
      loading: false,
      data: action.requestData,
      error: null,
    },
  }),
  [types.PROPERTY_DATA_REQUEST_ERROR]: (state, action) => ({
    ...state,
    propertyDataRequest: {
      loading: false,
      data: null,
      error: action.error,
    },
  }),
  [types.PROPERTY_DATA_RESULT_LOADING]: (state) => ({
    ...state,
    propertyDataResult: {
      loading: true,
      data: null,
      error: null,
    },
  }),
  [types.PROPERTY_DATA_RESULT_SUCCESS]: (state, action) => ({
    ...state,
    propertyDataResult: {
      loading: false,
      data: action.resultData,
      error: null,
      evImageLatest: action.evImageLatest,
    },
  }),
  [types.PROPERTY_DATA_RESULT_ERROR]: (state, action) => ({
    ...state,
    propertyDataResult: {
      loading: false,
      data: null,
      error: action.error,
    },
  }),
  [types.PRIMARY_SUBSCRIPTION_PRODUCTS_WITH_USAGE_SUCCESS]: (state, action) => ({
    ...state,
    primarySubscriptionProductsWithUsage: action.primarySubscriptionProductsWithUsage,
    subscriptionPlanMode: action.subscriptionPlanMode ?? null,
    bundleUsage: action.bundleUsage ?? null,
  }),
};

export default (state = initState, action = {}) => {
  const handler = actionHandlers[action.type];
  return handler ? handler(state, action) : state;
};
